import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Dna,
  FlaskConical,
  ExternalLink,
  Download,
  Eye,
  Network,
  Mountain,
  Waves,
  Database,
  Beaker,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Skeleton, SkeletonCard } from '../components/ui/Skeleton'
import { InteractionNetwork } from '../components/InteractionNetwork'
import { useCluster, useClusterComplex } from '../api/hooks'
import type { CompoundInfo } from '../types'
import {
  getMibigUrl,
  getNcbiTaxonomyUrl,
  getUniprotUrl,
  getNcbiProteinUrl,
  getPrimaryCompoundLink,
  getCompoundLinks,
} from '../utils/externalLinks'
import { exportClusterData } from '../utils/exportData'
import { InlineCopy } from '../components/ui/CopyButton'

export function ClusterDetailPage() {
  const { clusterId } = useParams<{ clusterId: string }>()
  const { data: cluster, isLoading: clusterLoading } = useCluster(clusterId || '')
  const { data: complexData } = useClusterComplex(clusterId || '')
  // Genes are now included in cluster response
  const genes = cluster?.genes
  const genesLoading = clusterLoading

  if (clusterLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-white/5 rounded animate-pulse" />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    )
  }

  if (!cluster) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-white mb-4">Cluster not found</h2>
        <Link to="/clusters">
          <Button variant="secondary" leftIcon={<ArrowLeft className="w-4 h-4" />}>
            Back to Browser
          </Button>
        </Link>
      </div>
    )
  }

  const getTypeVariant = (type: string) => {
    if (type?.includes('NRPS')) return 'purple'
    if (type?.includes('PKS')) return 'cyan'
    if (type?.includes('RiPP')) return 'green'
    if (type?.includes('Terpene')) return 'amber'
    return 'default'
  }

  // Get biosyn_class display text
  const biosynClass = cluster.biosyn_class?.join(', ') || 'Unknown Type'

  return (
    <div className="space-y-3 lg:space-y-4">
      {/* Compact Header: Breadcrumb + Title + Actions in one row */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center justify-between gap-2"
      >
        <div className="flex items-center gap-4 flex-wrap">
          <Link
            to="/clusters"
            className="inline-flex items-center gap-1 text-gray-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-3 h-3" />
            Back
          </Link>
          <h1 className="text-2xl lg:text-3xl font-bold text-white font-mono">
            <InlineCopy text={cluster.mibig_accession}>
              {cluster.mibig_accession}
            </InlineCopy>
          </h1>
          <a
            href={getNcbiTaxonomyUrl(cluster.ncbi_tax_id)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-base lg:text-lg text-gray-400 italic hover:text-accent-cyan transition-colors"
          >
            {cluster.organism_name}
          </a>
          <div className="flex flex-wrap gap-1">
            {cluster.biosyn_class?.map((cls, i) => (
              <Badge key={i} variant={getTypeVariant(cls)} size="sm">
                {cls}
              </Badge>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<ExternalLink className="w-3 h-3" />}
            onClick={() => window.open(getMibigUrl(cluster.mibig_accession, cluster.mibig_version), '_blank')}
          >
            View on MIBiG
          </Button>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download className="w-3 h-3" />}
            onClick={() => exportClusterData(cluster, genes || [])}
          >
            Export Data
          </Button>
        </div>
      </motion.div>

      {/* Cluster Info + Compounds - Compact inline layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <Card className="lg:col-span-2">
          <CardHeader className="py-2 px-4">
            <CardTitle className="text-sm">Cluster Information</CardTitle>
          </CardHeader>
          <CardContent className="py-2 px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-400">Biosynthetic Class</p>
                <p className="text-white">{biosynClass}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">NCBI Taxonomy ID</p>
                <a
                  href={getNcbiTaxonomyUrl(cluster.ncbi_tax_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-cyan hover:underline font-mono inline-flex items-center gap-1"
                >
                  {cluster.ncbi_tax_id || 'N/A'}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div>
                <p className="text-xs text-gray-400">MIBiG Version</p>
                <p className="text-white font-mono">{cluster.mibig_version || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Gene Count</p>
                <p className="text-white">{cluster.gene_count || 0} genes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Compounds Card - More compact */}
        <Card className="lg:col-span-2">
          <CardHeader className="py-2 px-4">
            <CardTitle className="flex items-center gap-2 text-sm">
              <FlaskConical className="w-4 h-4 text-accent-amber" />
              Compounds ({cluster.compounds?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2 px-4 max-h-24 overflow-y-auto">
            {cluster.compounds && cluster.compounds.length > 0 ? (
              <ul className="space-y-1.5">
                {cluster.compounds.map((compound: CompoundInfo, idx: number) => {
                  const primaryLink = getPrimaryCompoundLink(compound.database_id)
                  const allLinks = getCompoundLinks(compound.database_id)

                  return (
                    <li key={idx} className="flex items-center gap-2 flex-wrap">
                      <div className="w-1.5 h-1.5 rounded-full bg-accent-amber flex-shrink-0" />
                      {primaryLink ? (
                        <a
                          href={primaryLink.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white hover:text-accent-amber transition-colors inline-flex items-center gap-1 text-sm"
                        >
                          {compound.name}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-white text-sm">{compound.name}</span>
                      )}
                      {/* Show database links inline */}
                      {allLinks.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {allLinks.map((link, linkIdx) => (
                            <a
                              key={linkIdx}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs px-1.5 py-0.5 bg-white/5 rounded text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                            >
                              {link.type}
                            </a>
                          ))}
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">No compounds recorded</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Genes and Protein Interaction Network - Side by Side Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {/* Genes Section */}
        <Card className={complexData && complexData.interactions && complexData.interactions.length > 0 ? 'xl:col-span-1' : 'xl:col-span-2'}>
          <CardHeader className="py-2 px-4">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Dna className="w-4 h-4 text-accent-purple" />
              Genes ({genes?.length || 0})
            </CardTitle>
          </CardHeader>
          {/* Fixed max-height to ensure page fits without scrolling on most screens */}
          <CardContent className="max-h-[200px] lg:max-h-[240px] xl:max-h-[300px] 2xl:max-h-[500px] overflow-y-auto py-2 px-4">
            {genesLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : genes && genes.length > 0 ? (
              <div className="space-y-2">
                {genes.map((gene) => (
                  <motion.div
                    key={gene.protein_id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="p-2.5 bg-white/5 rounded-lg border border-white/5 hover:border-white/10 transition-colors"
                  >
                    {/* Top row: Protein ID, UniProt links, View Details button */}
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Protein ID with NCBI link and copy button */}
                        <InlineCopy text={gene.protein_id}>
                          <a
                            href={getNcbiProteinUrl(gene.protein_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-accent-cyan hover:underline inline-flex items-center gap-1 text-sm font-semibold"
                          >
                            {gene.protein_id}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </InlineCopy>
                        {/* UniProt links */}
                        {gene.uniprot_ids && gene.uniprot_ids.length > 0 && (
                          <div className="flex gap-1">
                            {gene.uniprot_ids.slice(0, 2).map((uid, i) => (
                              <a
                                key={i}
                                href={getUniprotUrl(uid)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center"
                              >
                                <Badge variant="default" size="sm" className="hover:bg-white/20 cursor-pointer text-xs">
                                  {uid}
                                </Badge>
                              </a>
                            ))}
                            {gene.uniprot_ids.length > 2 && (
                              <Badge variant="default" size="sm">+{gene.uniprot_ids.length - 2}</Badge>
                            )}
                          </div>
                        )}
                      </div>
                      <Link to={`/proteins/${clusterId}/${gene.protein_id}`}>
                        <Button variant="secondary" size="sm" leftIcon={<Eye className="w-3 h-3" />} className="text-xs py-1 px-2">
                          View Details
                        </Button>
                      </Link>
                    </div>

                    {/* Product description */}
                    <p className="text-xs text-gray-400 mt-1 line-clamp-1">
                      {gene.product || 'No description'}
                    </p>

                    {/* Analysis links in a compact row */}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {/* Domains count */}
                      {gene.domain_count !== undefined && gene.domain_count > 0 && (
                        <Link
                          to={`/proteins/${clusterId}/${gene.protein_id}`}
                          className="flex items-center gap-1 px-2 py-1 bg-gray-500/10 hover:bg-gray-500/20 rounded transition-colors"
                        >
                          <Database className="w-3 h-3 text-gray-400" />
                          <span className="text-xs text-gray-300">{gene.domain_count} dom</span>
                        </Link>
                      )}

                      {/* PDB hits */}
                      {gene.pdb_hit_count !== undefined && gene.pdb_hit_count > 0 && (
                        <Link
                          to={`/proteins/${clusterId}/${gene.protein_id}/pdb-hits`}
                          className="flex items-center gap-1 px-2 py-1 bg-accent-amber/10 hover:bg-accent-amber/20 rounded transition-colors"
                        >
                          <Beaker className="w-3 h-3 text-accent-amber" />
                          <span className="text-xs text-accent-amber">{gene.pdb_hit_count} PDB</span>
                        </Link>
                      )}

                      {/* SwissProt hits */}
                      {gene.swissprot_hit_count !== undefined && gene.swissprot_hit_count > 0 && (
                        <Link
                          to={`/proteins/${clusterId}/${gene.protein_id}/swissprot-hits`}
                          className="flex items-center gap-1 px-2 py-1 bg-accent-purple/10 hover:bg-accent-purple/20 rounded transition-colors"
                        >
                          <FlaskConical className="w-3 h-3 text-accent-purple" />
                          <span className="text-xs text-accent-purple">{gene.swissprot_hit_count} SP</span>
                        </Link>
                      )}

                      {/* FUJISAN - Structure-based function prediction */}
                      {gene.fujisan_hit_count !== undefined && gene.fujisan_hit_count > 0 && (
                        <Link
                          to={`/proteins/${clusterId}/${gene.protein_id}/fujisan-hits`}
                          className="flex items-center gap-1 px-2 py-1 bg-accent-green/10 hover:bg-accent-green/20 rounded transition-colors border border-accent-green/30"
                        >
                          <Mountain className="w-3 h-3 text-accent-green" />
                          <span className="text-xs text-accent-green">{gene.fujisan_hit_count} FUJ</span>
                        </Link>
                      )}

                      {/* UNKAI - Embedding-based function prediction */}
                      {gene.unkai_hit_count !== undefined && gene.unkai_hit_count > 0 && (
                        <Link
                          to={`/proteins/${clusterId}/${gene.protein_id}/unkai-hits`}
                          className="flex items-center gap-1 px-2 py-1 bg-accent-cyan/10 hover:bg-accent-cyan/20 rounded transition-colors border border-accent-cyan/30"
                        >
                          <Waves className="w-3 h-3 text-accent-cyan" />
                          <span className="text-xs text-accent-cyan">{gene.unkai_hit_count} UNK</span>
                        </Link>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No genes found for this cluster</p>
            )}
          </CardContent>
        </Card>

        {/* Protein Interaction Network Section */}
        {complexData && complexData.interactions && complexData.interactions.length > 0 ? (
          <Card className="xl:col-span-1">
          <CardHeader className="py-2 px-4">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Network className="w-4 h-4 text-accent-green" />
              Protein Interaction Network ({complexData.total} interactions)
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2 px-4">
            {/* Interactive Network Graph */}
            <InteractionNetwork
              clusterId={clusterId || ''}
              interactions={complexData.interactions}
              className="mb-2"
            />

            <p className="text-xs text-gray-400 mb-2">
              Click on a protein node to view details. Click on an edge to view complex prediction.
            </p>

            {/* Collapsible Interactions Table */}
            <details className="group">
              <summary className="flex items-center gap-2 cursor-pointer text-gray-400 hover:text-white transition-colors">
                <ChevronDown className="w-3 h-3 group-open:hidden" />
                <ChevronUp className="w-3 h-3 hidden group-open:block" />
                <span className="text-xs">Show interaction table</span>
              </summary>
              <div className="overflow-x-auto overflow-y-auto max-h-[300px] mt-4">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-dark-bg-secondary z-10">
                    <tr className="text-left text-gray-400 border-b border-white/10">
                      <th className="py-3 px-2">Type</th>
                      <th className="py-3 px-2">Protein 1</th>
                      <th className="py-3 px-2">Protein 2</th>
                      <th className="py-3 px-2 text-right">ipSAE</th>
                      <th className="py-3 px-2 text-right">ipSAE_min</th>
                      <th className="py-3 px-2 text-right">ipTM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {complexData.interactions.map((interaction, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                        onClick={() => {
                          window.location.href = `/clusters/${clusterId}/complex/${interaction.protein1_id}/${interaction.protein2_id}`
                        }}
                      >
                        <td className="py-2 px-2">
                          <Badge variant={interaction.is_homo ? 'cyan' : 'purple'} size="sm">
                            {interaction.is_homo ? 'Homo' : 'Hetero'}
                          </Badge>
                        </td>
                        <td className="py-2 px-2">
                          <div className="text-accent-cyan">
                            <span className="font-mono text-xs">{interaction.protein1_id}</span>
                          </div>
                          <div className="text-gray-500 text-xs truncate max-w-[150px]" title={interaction.protein1_product}>
                            {interaction.protein1_product}
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          <div className="text-accent-cyan">
                            <span className="font-mono text-xs">{interaction.protein2_id}</span>
                          </div>
                          <div className="text-gray-500 text-xs truncate max-w-[150px]" title={interaction.protein2_product}>
                            {interaction.protein2_product}
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-gray-300">
                          {interaction.ipsae.toFixed(2)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-gray-300">
                          {interaction.ipsae_min.toFixed(2)}
                        </td>
                        <td className="py-2 px-2 text-right font-mono">
                          <span
                            className={
                              interaction.iptm >= 0.8
                                ? 'text-accent-green'
                                : interaction.iptm >= 0.7
                                  ? 'text-accent-amber'
                                  : 'text-gray-300'
                            }
                          >
                            {interaction.iptm.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            <p className="text-xs text-gray-500 mt-2">
              Protein-protein interactions predicted by AlphaFold2 multimer. Only interactions with ipTM ≥ 0.6 are shown.
            </p>
          </CardContent>
        </Card>
        ) : (
          /* Empty placeholder when no complex data - genes section will take full width via CSS */
          null
        )}
      </div>
    </div>
  )
}
